import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'underscore';
import classnames from 'classnames';

import { Image as FluentImage, ImageFit, ImageLoadState } from '@bingads-webui-react/fabric-control';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  TilesGridCellActionButton as ActionButton,
} from './tiles-grid-cell-action-button';

import { getTilesGridCellImageStyles } from '../styles/tiles-grid-cell-image';

const RETRY_COUNT = 3;
const RETRY_DEALY = 1000;

const CellImage = ({
  src,
  alt,
  showSpinner,
  onLoaded,
  onError,
  i18n,
  onClick,
  enableRetry,
  classes,
  onCustomizedSpinner,
  imageFit,
}) => {
  //
  const [imageLoadState, setImageLoadState] = useState(ImageLoadState.notLoaded);

  const onLoadingStateChange = useCallback((s, onLoadError) => {
    if (s === ImageLoadState.loaded) {
      onLoaded(true);
    } else if (s === ImageLoadState.error) {
      onLoadError();
      // don't set error state until all retrying are failed
      return;
    }
    setImageLoadState(s);
  }, [onLoaded]);

  // retry image loading by creating a HTML Image element
  const reloadImage = useCallback((onLoadError) => {
    const img = new Image();
    img.onerror = () => {
      onLoadingStateChange(ImageLoadState.error, onLoadError);
    };
    img.onload = () => {
      onLoadingStateChange(ImageLoadState.loaded);
    };
    onLoadingStateChange(ImageLoadState.notLoaded);
    img.src = src;
  }, [onLoadingStateChange, src]);

  const onErrorWithRetry = useCallback((retryCount = RETRY_COUNT) => {
    if (enableRetry && retryCount > 0) {
      setTimeout(() => reloadImage(() => onErrorWithRetry(retryCount - 1)), RETRY_DEALY);
    } else {
      setImageLoadState(ImageLoadState.error);
      if (onError) {
        onError();
      }
    }
  }, [enableRetry, onError, reloadImage]);

  const onReloadImage = useCallback((event) => {
    if (imageLoadState === ImageLoadState.error) {
      reloadImage(onErrorWithRetry);
    }
    if (event.stopPropagation) { event.stopPropagation(); }
    if (event.preventDefault) { event.preventDefault(); }
  }, [imageLoadState, onErrorWithRetry, reloadImage]);

  const onImageLoadingStateChange = useCallback((s) => {
    onLoadingStateChange(s, onErrorWithRetry);
  }, [onErrorWithRetry, onLoadingStateChange]);

  return (
    <div className={classnames('cell-image-frame', classes.containerClass, classes.tilesGridCellImageContainer)}>
      <div className={classnames('cell-image-state', classes.imageStateOverlayClass)}>
        <div className={classes.stateParent}>
          {imageLoadState === ImageLoadState.notLoaded && showSpinner && (onCustomizedSpinner ?
            onCustomizedSpinner() : (<AssetLibrarySpinner />))
          }
          { imageLoadState === ImageLoadState.error && (
            <ActionButton
              text={i18n.getString(_TL_('Reload'))}
              iconName="Refresh"
              onClick={onReloadImage}
              primary={false}
              className={classes.reloadBtn}
            />
          )}
        </div>
      </div>
      <FluentImage
        className={classes.imageClass}
        imageFit={imageFit}
        src={src}
        alt={alt}
        maximizeFrame
        onLoadingStateChange={onImageLoadingStateChange}
        onClick={onClick}
      />
    </div>
  );
};

CellImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  showSpinner: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onLoaded: PropTypes.func,
  onError: PropTypes.func,
  onClick: PropTypes.func,
  onCustomizedSpinner: PropTypes.func,
  enableRetry: PropTypes.bool,
  classes: PropTypes.shape({
    tilesGridCellImageContainer: PropTypes.string,
    containerClass: PropTypes.string,
    imageClass: PropTypes.string,
    imageStateOverlayClass: PropTypes.string,
    stateParent: PropTypes.string,
    reloadBtn: PropTypes.string,
  }).isRequired,
  imageFit: PropTypes.number,
};

CellImage.defaultProps = {
  showSpinner: true,
  onLoaded: noop,
  onError: noop,
  onClick: noop,
  enableRetry: true,
  onCustomizedSpinner: undefined,
  imageFit: ImageFit.cover,
};

export const TilesGridCellImage = withDefaultStyles(CellImage, getTilesGridCellImageStyles);
